import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, useTheme } from '@/App';
import { Button } from '@/components/ui/button';
import { MessageSquare, Sun, Moon, CheckCircle, Sparkles, ArrowRight } from 'lucide-react';

const PaymentSuccessPage = () => {
  const { user, refreshUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    refreshUser();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="glass sticky top-0 z-50 border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src="/queryverse-logo2.png" alt="QueryVerse" className="h-8" />
            </Link>

            <button
              onClick={toggleTheme}
              className="p-2 rounded-full hover:bg-secondary transition-colors"
              data-testid="success-theme-toggle"
            >
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="flex items-center justify-center min-h-[calc(100vh-73px)] px-6">
        <div className="max-w-md w-full text-center">
          {/* Success Icon */}
          <div className="relative mx-auto w-20 h-20 mb-8">
            <div className="absolute inset-0 bg-emerald-500/20 rounded-full animate-ping" />
            <div className="relative w-20 h-20 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center">
              <CheckCircle className="h-10 w-10 text-emerald-600 dark:text-emerald-400" />
            </div>
          </div>

          {/* Message */}
          <h1 className="text-3xl font-semibold tracking-tight font-['Manrope'] animate-fade-in">
            Payment Successful!
          </h1>
          <p className="mt-4 text-muted-foreground text-lg leading-relaxed animate-slide-up">
            Thank you for upgrading to <span className="font-medium text-foreground capitalize">{user?.subscription_plan}</span>. 
            You now have unlimited access to QueryVerse.
          </p>

          {/* Plan Details */}
          <div className="mt-8 p-6 rounded-2xl bg-secondary/30 border border-border/50 animate-slide-up">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Sparkles className="h-5 w-5 text-primary" />
              <span className="font-medium font-['Manrope']">Your Plan is Active</span>
            </div>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-center justify-center gap-2">
                <CheckCircle className="h-4 w-4 text-emerald-500" />
                Unlimited messages
              </li>
              <li className="flex items-center justify-center gap-2">
                <CheckCircle className="h-4 w-4 text-emerald-500" />
                Priority response time
              </li>
              <li className="flex items-center justify-center gap-2">
                <CheckCircle className="h-4 w-4 text-emerald-500" />
                Access to all features
              </li>
            </ul>
          </div>

          {/* CTA */}
          <div className="mt-8 space-y-4 animate-slide-up">
            <Button 
              size="lg" 
              onClick={() => navigate('/chat')} 
              className="rounded-full px-8 h-12"
              data-testid="start-chatting-btn"
            >
              Start Chatting
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
            <div>
              <Button 
                variant="link" 
                onClick={() => navigate('/billing')}
                className="text-muted-foreground"
                data-testid="view-billing-btn"
              >
                View Billing Details
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
